'use client'

import { useEffect, useState } from 'react'
import { Check, Users, Calendar, Clock } from 'lucide-react'

/**
 * Looping fake-video of the booking flow for the homepage hero.
 * Steps:
 *   0 — Datum/Zeit selection (a time tile pulses then highlights)
 *   1 — Form filling (name + email auto-type, then phone)
 *   2 — Confirmation card with green check + duration banner
 *   3 — Pause, then restart
 *
 * No external video file — pure React + CSS keyframes. Loops on a
 * fixed timer so visitors get a consistent demo whether they land
 * fresh or hit it after navigating back. Pauses on hover so they can
 * read frozen frames if curious.
 */
const STEPS = [
  { id: 0, label: 'Datum & Uhrzeit', durationMs: 3200 },
  { id: 1, label: 'Ihre Daten',     durationMs: 4400 },
  { id: 2, label: 'Bestätigung',    durationMs: 4000 },
] as const

const TIMES = ['18:00', '18:30', '19:00', '19:30', '20:00', '20:30']
const SELECTED_TIME_IDX = 4 // 20:00

export function AnimatedBookingFlow() {
  const [step, setStep] = useState(0)
  const [paused, setPaused] = useState(false)
  const [typedName, setTypedName] = useState('')
  const [typedEmail, setTypedEmail] = useState('')

  // Step cycle
  useEffect(() => {
    if (paused) return
    const next = (step + 1) % (STEPS.length + 1)   // +1 = brief pause beat
    const ms = STEPS[step]?.durationMs ?? 1800
    const t = setTimeout(() => {
      setStep(next % STEPS.length)
      if (next % STEPS.length === 0) {
        setTypedName('')
        setTypedEmail('')
      }
    }, ms)
    return () => clearTimeout(t)
  }, [step, paused])

  // Typewriter for step 1
  useEffect(() => {
    if (step !== 1 || paused) return
    const name = 'Familie Müller'
    const email = 'mueller@example.de'
    const ids: ReturnType<typeof setTimeout>[] = []

    name.split('').forEach((ch, i) => {
      ids.push(setTimeout(() => setTypedName(name.slice(0, i + 1)), 90 * i + 600))
    })
    email.split('').forEach((ch, i) => {
      ids.push(setTimeout(() => setTypedEmail(email.slice(0, i + 1)), 65 * i + 2200))
    })

    return () => ids.forEach(clearTimeout)
  }, [step, paused])

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative rounded-2xl border border-pub-rule bg-pub-surface overflow-hidden"
      style={{ boxShadow: '0 20px 60px -20px rgba(23,106,71,0.18)' }}>

      {/* Header: title + step pill */}
      <div className="flex items-center justify-between border-b border-pub-rule px-5 py-3 bg-pub-bg">
        <div className="flex items-center gap-2">
          <span className="flex h-2 w-2 rounded-full bg-brand-500 animate-pulse" />
          <p className="text-[12px] font-semibold text-pub-ink">Live-Demo · Online-Reservierung</p>
        </div>
        <div className="flex items-center gap-1">
          {STEPS.map(s => (
            <span key={s.id}
              className={`h-1 w-6 rounded-full transition-colors ${
                step === s.id ? 'bg-brand-600' : step > s.id ? 'bg-brand-300' : 'bg-pub-rule'
              }`} />
          ))}
        </div>
      </div>

      {/* Body slides between three states; absolute positioning keeps the
          frame height stable so the homepage doesn't reflow. */}
      <div className="relative" style={{ height: 380 }}>

        {/* STEP 0 — pick time */}
        <Pane visible={step === 0}>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-pub-rule bg-pub-bg p-3">
                <p className="text-[10px] uppercase tracking-widest text-pub-muted mb-1">Personen</p>
                <p className="text-base font-semibold text-pub-ink flex items-center gap-2">
                  <Users className="h-4 w-4 text-pub-muted" /> 4 Personen
                </p>
              </div>
              <div className="rounded-lg border border-pub-rule bg-pub-bg p-3">
                <p className="text-[10px] uppercase tracking-widest text-pub-muted mb-1">Datum</p>
                <p className="text-base font-semibold text-pub-ink flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-pub-muted" /> Heute
                </p>
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-pub-muted mb-2 flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Verfügbare Zeiten
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {TIMES.map((t, i) => {
                  const isSelected = i === SELECTED_TIME_IDX
                  return (
                    <div key={t}
                      className={`rounded-md py-2 text-center text-[12px] font-semibold transition-all ${
                        isSelected
                          ? 'bg-brand-600 text-white scale-105'
                          : 'border border-pub-rule bg-white text-pub-ink'
                      }`}
                      style={{
                        animationName: isSelected ? 'rzr-pick' : undefined,
                        animationDuration: '1.6s',
                        animationDelay: `${1.2 + i * 0.08}s`,
                        animationFillMode: 'both',
                      }}>
                      {t}
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="rounded-md bg-brand-600 py-3 text-center text-[13px] font-semibold text-white"
              style={{ animation: 'rzr-cta-pop 0.5s ease-out 2.6s both' }}>
              Weiter →
            </div>
          </div>
        </Pane>

        {/* STEP 1 — fill form */}
        <Pane visible={step === 1}>
          <div className="p-5 space-y-3">
            <div className="text-[10px] text-pub-muted mb-1">
              4 Personen · 20:00 · Heute
            </div>
            <Field label="Name *" value={typedName} cursor={typedName.length > 0 && typedName.length < 14} />
            <Field label="E-Mail *" value={typedEmail} cursor={typedEmail.length > 0 && typedEmail.length < 18} />
            <Field label="Telefon"  value={typedEmail.length >= 18 ? '+49 30 …' : ''} cursor={false} placeholder="+49 …" />
            <Field label="Anmerkungen (optional)" value="" cursor={false} placeholder="Allergie, Geburtstag…" />
            <div className={`mt-1 rounded-md py-3 text-center text-[13px] font-semibold transition-all ${
              typedEmail.length >= 18 ? 'bg-brand-600 text-white' : 'bg-pub-rule text-pub-muted'
            }`}>
              {typedEmail.length >= 18 ? 'Reservieren →' : 'Reservieren'}
            </div>
          </div>
        </Pane>

        {/* STEP 2 — confirmation */}
        <Pane visible={step === 2}>
          <div className="p-5 flex flex-col items-center text-center pt-10">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-100 mb-4"
              style={{ animation: 'rzr-pop 0.5s ease-out both' }}>
              <Check className="h-7 w-7 text-brand-700" strokeWidth={2.5} />
            </div>
            <h3 className="font-display text-2xl text-pub-ink leading-tight"
              style={{ fontFamily: 'var(--font-display)' }}>
              Reservierung bestätigt!
            </h3>
            <p className="mt-2 text-[13px] text-pub-muted">
              4 Personen · 20:00 · Heute
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-brand-50 border border-brand-100 px-3 py-1 text-[11px] text-brand-900">
              <Clock className="h-3 w-3" />
              Reserviert für <strong>90 Minuten</strong>
            </div>
            <p className="mt-5 text-[11px] text-pub-muted">
              Bestätigung an <strong className="text-pub-ink">mueller@example.de</strong> gesendet
            </p>
          </div>
        </Pane>
      </div>

      <style>{`
        @keyframes rzr-pick {
          0%, 60%   { transform: scale(1);     box-shadow: 0 0 0 0 rgba(23,106,71,0); }
          70%       { transform: scale(1.08);  box-shadow: 0 0 0 6px rgba(23,106,71,0.18); }
          100%      { transform: scale(1.05);  box-shadow: 0 0 0 0 rgba(23,106,71,0); }
        }
        @keyframes rzr-cta-pop { from { transform: translateY(6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes rzr-pop { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  )
}

function Pane({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <div
      aria-hidden={!visible}
      className="absolute inset-0 transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        pointerEvents: visible ? 'auto' : 'none',
      }}>
      {children}
    </div>
  )
}

function Field({ label, value, cursor, placeholder }: { label: string; value: string; cursor: boolean; placeholder?: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-pub-muted mb-1">{label}</p>
      <div className="h-9 rounded-md border border-pub-rule bg-white px-3 flex items-center text-[13px] text-pub-ink">
        {value
          ? <>{value}{cursor && <span className="ml-px inline-block w-px h-3.5 bg-brand-600 animate-pulse" />}</>
          : <span className="text-pub-muted/60">{placeholder ?? ''}</span>}
      </div>
    </div>
  )
}
